// sizePalette.tsx — Common size quick-select palettes for Doors, Windows, and Rough Openings.
// Reuses EmbedPalette-compatible CSS classes (.embed-palette*, .embed-card*) — no new styles needed.

import { useState } from 'react';

export interface SizePreset {
  label:  string;   // Short chip label, e.g. "3'×7'"
  desc:   string;   // Tooltip / card subtitle, e.g. "36″×84″ Single"
  width:  number;   // inches
  height: number;   // inches
  type?:  string;   // Optional entity type hint (e.g. 'double', 'overhead')
}

// ── Common man door sizes ────────────────────────────────────────────────────
export const DOOR_PRESETS: SizePreset[] = [
  { label: "3'×7'",     desc: '36″×84″ Single',    width:  36, height:  84, type: 'single'   },
  { label: "3'×8'",     desc: '36″×96″ Single',    width:  36, height:  96, type: 'single'   },
  { label: "3'6\"×7'",  desc: '42″×84″ Single',    width:  42, height:  84, type: 'single'   },
  { label: "3'6\"×8'",  desc: '42″×96″ Single',    width:  42, height:  96, type: 'single'   },
  { label: "4'×7'",     desc: '48″×84″ Single',    width:  48, height:  84, type: 'single'   },
  { label: "4'×8'",     desc: '48″×96″ Single',    width:  48, height:  96, type: 'single'   },
  { label: "6'×7'",     desc: '72″×84″ Double',    width:  72, height:  84, type: 'double'   },
  { label: "6'×8'",     desc: '72″×96″ Double',    width:  72, height:  96, type: 'double'   },
  { label: "8'×8'",     desc: '96″×96″ Overhead',  width:  96, height:  96, type: 'overhead' },
  { label: "10'×10'",   desc: '120″×120″ Overhead', width: 120, height: 120, type: 'overhead' },
  { label: "12'×12'",   desc: '144″×144″ Overhead', width: 144, height: 144, type: 'overhead' },
];

// ── Common window sizes ──────────────────────────────────────────────────────
export const WINDOW_PRESETS: SizePreset[] = [
  { label: "2'×2'", desc: '24″×24″', width:  24, height:  24 },
  { label: "2'×4'", desc: '24″×48″', width:  24, height:  48 },
  { label: "3'×3'", desc: '36″×36″', width:  36, height:  36 },
  { label: "3'×4'", desc: '36″×48″', width:  36, height:  48 },
  { label: "3'×5'", desc: '36″×60″', width:  36, height:  60 },
  { label: "4'×4'", desc: '48″×48″', width:  48, height:  48 },
  { label: "4'×5'", desc: '48″×60″', width:  48, height:  60 },
  { label: "4'×6'", desc: '48″×72″', width:  48, height:  72 },
  { label: "5'×5'", desc: '60″×60″', width:  60, height:  60 },
  { label: "6'×6'", desc: '72″×72″', width:  72, height:  72 },
  { label: "6'×8'", desc: '72″×96″', width:  72, height:  96 },
];

// ── Common rough opening sizes ───────────────────────────────────────────────
// Rough openings are typically 2″ wider and 2″ taller than the finish opening.
export const RO_PRESETS: SizePreset[] = [
  { label: "3' dr",     desc: "38″×86″ (for 3'×7' door)",  width:  38, height:  86 },
  { label: "3'×8' dr",  desc: "38″×98″ (for 3'×8' door)",  width:  38, height:  98 },
  { label: "4' dr",     desc: "50″×86″ (for 4'×7' door)",  width:  50, height:  86 },
  { label: "6' dbl",    desc: "74″×86″ (for 6' dbl door)", width:  74, height:  86 },
  { label: "2' win",    desc: "26″×26″ (for 2'×2' win)",   width:  26, height:  26 },
  { label: "3' win",    desc: "38″×50″ (for 3'×4' win)",   width:  38, height:  50 },
  { label: "4' win",    desc: "50″×50″ (for 4'×4' win)",   width:  50, height:  50 },
  { label: "4'×5' win", desc: "50″×62″ (for 4'×5' win)",   width:  50, height:  62 },
];

// ── SizePalette component ────────────────────────────────────────────────────
// Renders identically to EmbedPalette — same CSS classes, same collapsible chip grid.
// A trailing "Custom" chip closes the palette so the user types manually in SlotEditor.

interface SizePaletteProps {
  title:    string;
  presets:  SizePreset[];
  onSelect: (preset: SizePreset) => void;
}

export function SizePalette({ title, presets, onSelect }: SizePaletteProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="embed-palette">
      <button
        className="embed-palette-toggle"
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        📐 {title} <span className="embed-palette-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="embed-palette-grid">
          {presets.map(p => (
            <button
              key={p.label}
              type="button"
              className="embed-card"
              title={p.desc}
              onClick={() => { onSelect(p); setOpen(false); }}
            >
              <span className="embed-card-label">{p.label}</span>
              <span className="embed-card-desc">{p.desc}</span>
            </button>
          ))}
          <button
            type="button"
            className="embed-card"
            title="Enter custom dimensions manually below"
            onClick={() => setOpen(false)}
          >
            <span className="embed-card-label">Custom</span>
            <span className="embed-card-desc">Type manually below</span>
          </button>
        </div>
      )}
    </div>
  );
}
